/**
 * The one place that talks to the server.
 *
 * Holds the access token in memory and the refresh token in localStorage, refreshes
 * transparently on a 401, and turns the server's `{ error: { code, message } }` shape
 * into an ApiError the UI can show verbatim — the server writes the message, because
 * the server is where the rule lives.
 */
import { cache, setCacheOwner } from './idb'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the server refused on a business rule rather than a technical fault. */
  get isRuleViolation() {
    return this.status === 422 || this.status === 409
  }
}

const REFRESH_KEY = 'ledger.refresh'
const USER_KEY = 'ledger.user'
const DEVICE_KEY = 'ledger.device'

export interface SessionUser {
  id: string
  fullName: string
  email?: string
  msisdn?: string
  roles: string[]
  homeDepotId?: string
  org: { id: string; code: string; name: string }
}

let accessToken: string | null = null
let refreshing: Promise<boolean> | null = null

export const session = {
  get user(): SessionUser | null {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as SessionUser) : null
  },
  get refreshToken() {
    return localStorage.getItem(REFRESH_KEY)
  },
  get deviceId() {
    let id = localStorage.getItem(DEVICE_KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(DEVICE_KEY, id)
    }
    return id
  },
  get isSignedIn() {
    return !!localStorage.getItem(REFRESH_KEY)
  },
  set(user: SessionUser, access: string, refresh: string) {
    accessToken = access
    localStorage.setItem(USER_KEY, JSON.stringify(user))
    localStorage.setItem(REFRESH_KEY, refresh)
    setCacheOwner(user.id)
  },
  clear() {
    accessToken = null
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(REFRESH_KEY)
    // The cached reads go with the session. Leaving them would hand the next person
    // to sign in on this phone the last person's round, farmer names and amounts.
    setCacheOwner(null)
    void cache.clear()
  },
  /** Roles the caller holds, used to decide which shell and which nav to show. */
  has(...roles: string[]) {
    const mine = this.user?.roles ?? []
    return roles.some((r) => mine.includes(r))
  },
}

async function parse(res: Response) {
  const text = await res.text()
  if (!text.trim()) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function refreshAccessToken(): Promise<boolean> {
  const token = session.refreshToken
  if (!token) return false

  // Collapse concurrent 401s onto one refresh.
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: token, deviceId: session.deviceId }),
        })
        if (!res.ok) {
          session.clear()
          return false
        }
        const data = await res.json()
        accessToken = data.accessToken
        localStorage.setItem(REFRESH_KEY, data.refreshToken)
        return true
      } catch {
        // Offline: keep the session so the field app can carry on with cached data.
        return false
      } finally {
        refreshing = null
      }
    })()
  }
  return refreshing
}

async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError(0, 'OFFLINE', 'No connection. Your work is saved and will send when you are back online.')
  }

  if (res.status === 401 && retry && (await refreshAccessToken())) {
    return request<T>(method, path, body, false)
  }

  const payload = await parse(res)

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string } })?.error
    throw new ApiError(
      res.status,
      err?.code ?? `HTTP_${res.status}`,
      err?.message ?? (res.status === 403
        ? 'You do not have permission to do that.'
        : 'Something went wrong.'),
    )
  }

  return payload as T
}

/**
 * A multipart upload carrying the staff token, with the same refresh-on-401 retry as
 * every other call. It cannot go through request(): setting a Content-Type here would
 * stop the browser writing the multipart boundary, and the body is not JSON.
 */
async function upload(path: string, form: FormData, retry = true): Promise<Response> {
  const headers: Record<string, string> = {}
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  const res = await fetch(`/api${path}`, { method: 'POST', headers, body: form })

  if (res.status === 401 && retry && (await refreshAccessToken())) {
    return upload(path, form, false)
  }
  return res
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),

  async login(identifier: string, password: string) {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        identifier,
        password,
        deviceId: session.deviceId,
        deviceLabel: navigator.userAgent.slice(0, 60),
      }),
    })
    const payload = await parse(res)
    if (!res.ok) {
      const err = (payload as { error?: { code?: string; message?: string } })?.error
      throw new ApiError(res.status, err?.code ?? 'LOGIN_FAILED',
        err?.message ?? 'Could not sign in.')
    }
    const data = payload as { accessToken: string; refreshToken: string; user: SessionUser }
    session.set(data.user, data.accessToken, data.refreshToken)
    return data.user
  },

  async logout() {
    const token = session.refreshToken
    session.clear()
    if (token) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: token }),
        })
      } catch {
        /* signing out locally is enough */
      }
    }
  },

  /** Sends a file with the signed-in staff member's token. */
  upload,

  /** An authenticated request that returns the raw response — for bytes, not JSON. */
  async fetch(path: string, retry = true): Promise<Response> {
    const headers: Record<string, string> = {}
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`
    const res = await window.fetch(`/api${path}`, { headers })
    if (res.status === 401 && retry && (await refreshAccessToken())) {
      return api.fetch(path, false)
    }
    return res
  },

  /** Restores the in-memory access token after a page reload. */
  async resume() {
    if (!session.isSignedIn) return false
    // A reload loses the in-memory owner along with the token; without this the cache
    // would quietly stop working for the rest of the session.
    setCacheOwner(session.user?.id ?? null)
    return refreshAccessToken()
  },
}
